import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  showErrorToast,
  showSuccessToast,
} from '../../../../app/components/SnackBar';
import { getApiHost } from '../../../../app/infrastructure/http/apiClient';
import i18n from '../../../../i18n/i18n.module';
import { unwrapStudentMe } from '../../application/mapStudentProfile';
import type {
  StudentMe,
  UpdateStudentMeRequest,
} from '../../application/types';

type PublicSlugCheck = {
  slug: string;
  available: boolean;
  reason: 'current' | 'available' | 'invalid' | 'reserved' | 'taken';
};

function authorizationHeader(token?: string | null): string | undefined {
  if (!token) {
    return undefined;
  }
  return token.startsWith('Bearer ') ? token : `Bearer ${token}`;
}

function unwrapSlugCheck(response: unknown): PublicSlugCheck {
  if (response && typeof response === 'object' && 'data' in response) {
    const data = (response as { data?: PublicSlugCheck }).data;
    if (data && typeof data.available === 'boolean') return data;
  }
  return response as PublicSlugCheck;
}

function apiBaseUrl(): string {
  const host = getApiHost();
  return host.endsWith('/') ? host : `${host}/`;
}

export const studentProfileApi = createApi({
  reducerPath: 'studentProfileApi',
  baseQuery: fetchBaseQuery({
    baseUrl: apiBaseUrl(),
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
      const token = (
        getState() as { authorization?: { accessToken?: string | null } }
      ).authorization?.accessToken;
      const value = authorizationHeader(token);
      if (value) {
        headers.set('Authorization', value);
      }
      return headers;
    },
  }),
  tagTypes: ['StudentMe'],
  endpoints: (build) => ({
    getStudentById: build.query<StudentMe, number>({
      query: (id) => ({
        url: `students/${id}`,
        method: 'GET',
      }),
      transformResponse: unwrapStudentMe,
    }),
    getStudentMe: build.query<StudentMe, void>({
      query: () => ({
        url: 'students/me',
        method: 'GET',
      }),
      transformResponse: unwrapStudentMe,
      providesTags: ['StudentMe'],
    }),
    verifyStudentSlug: build.query<PublicSlugCheck, string>({
      query: (slug) => ({
        url: 'students/me/slug',
        method: 'GET',
        params: { slug },
      }),
      transformResponse: unwrapSlugCheck,
    }),
    updateStudentMe: build.mutation<StudentMe, UpdateStudentMeRequest | FormData>({
      query: (body) => ({
        url: 'students/me',
        method: 'PATCH',
        body,
      }),
      transformResponse: unwrapStudentMe,
      invalidatesTags: ['StudentMe'],
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          showSuccessToast(i18n.t('student-profile.updated'));
        } catch {
          showErrorToast(i18n.t('student-profile.update-error'), undefined, 5000);
        }
      },
    }),
  }),
});

export const {
  useGetStudentByIdQuery,
  useGetStudentMeQuery,
  useUpdateStudentMeMutation,
  useLazyVerifyStudentSlugQuery,
} = studentProfileApi;
